<template>
    <div class="AdminEntityListScreen flex-col flex-1">
        <FadeTransition mode="out-in">
            <div class="AdminEntityListHeader">
                <Breadcrumbs :breadcrumbs="breadcrumbs" />
                <div class="flex row">
                    <Dropdown
                        :options="exportOptions"
                        @onClick="handleExport"
                    >
                        <WButton
                            outline
                            :iconLeft="exportLoading ? '' : 'download-cloud'"
                            :loading="exportLoading"
                            :disabled="exportLoading"
                            color="accent-blue"
                        >
                            {{ $t('entities.actions.export') }}
                        </WButton>
                    </Dropdown>
                    <WButton
                        iconLeft="plus-circle"
                        color="primary"
                        class="ml-1"
                        @click="$router.push({ name: 'AdminEntityItemCreate' })"
                    >
                        {{ $t('entities.actions.createRow') }}
                    </WButton>
                </div>
            </div>
        </FadeTransition>
        <FiltersTopbar
            ref="filtersTopbar"
            :entityKey="entityKey"
            :filterModel="filterModel"
            :totalResults="totalResults"
            :excludedEntities="excludedEntities"
            @remove="cleanFilter"
            @cleanAll="cleanAllFilters"
            @applyPreset="applyPreset"
            @refreshTable="filterTable"
        />
        <div class="flex-row flex-1">
            <FiltersSidebar
                :entityKey="entityKey"
                :isOpen="isFiltersSidebarOpen"
                :filterModel="filterModel"
                :columnDefs="columnDefs"
                :selectGroups="selectGroups"
                @applyFilter="applyFilter"
                @cleanFilter="cleanFilter"
                @cleanAllFilters="cleanAllFilters"
                @toggle="isFiltersSidebarOpen = !isFiltersSidebarOpen"
            />
            <WCard class="flex-1">
                <DataGrid
                    ref="dataGrid"
                    class="DataGrid"
                    :columnDefs="columnDefs"
                    :loading="isLoading"
                    :filterable="false"
                    v-bind="dataGridConfig"
                    @onGridReady="handleReady"
                    @onCellChanged="handleCellChanged"
                    @onCellMouseOver="handlePopup(true, $event)"
                    @onCellMouseOut="handlePopup(false, $event)"
                >
                    <template #top="{ grid: gridApi }">
                        <ViewsGrid
                            v-if="gridApi && columns.length"
                            ref="viewsGrid"
                            :value="activeView"
                            :grid="gridApi"
                            :entityKey="entityKey"
                            dropdownDirection="bottomRight"
                            @onChange="handleChangeView"
                        />
                    </template>
                </DataGrid>
            </WCard>
            <RowPopup
                v-if="popupProperties.length"
                :visible="popupVisible"
                :items="popupData"
            />
        </div>

        <ModalRequest
            :opened="modal === modals.MERGE_SELECTION"
            :title="$t('entities.actionsModals.mergeSelection.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isLoadingEditingCell"
            preventDefault
            @close="handleCleanModal"
            @onAccept="mergeSelecteds"
        />

        <ModalRequest
            :opened="modal === modals.DELETE_SELECTION"
            :title="$t('entities.actionsModals.deleteSelection.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isLoadingEditingCell"
            preventDefault
            @close="handleCleanModal"
            @onAccept="removeSelecteds"
        />

        <ModalRequest
            :opened="modal === modals.EDIT_SELECTION"
            :title="$t('entities.actionsModals.editSelection.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isLoadingEditingCell"
            preventDefault
            @close="handleCancelEdition"
            @onAccept="editSelected"
        />
        <Modal
            :opened="modal === modals.AUDIT"
            :title="$t('entities.actions.audit')"
            :dissmisable="dissmisableAudit"
            class="AuditModal"
            @close="handleCleanModal"
        >
            <template #body>
                <HistoricField
                    v-if="node"
                    :entityKey="entityKey"
                    :field="node.fieldKey"
                    :node="node.rows"
                    @onCancel="handleCleanModal"
                    @onChange="handleChangeField"
                />
            </template>
        </Modal>

        <NotificationLink
            :group="notification.group"
            position="bottom left"
        />
    </div>
</template>
<script>
import moment from 'moment'
import fileDownload from 'js-file-download'
import { mapState } from 'vuex'
import {
    Api, apiRequest, rethrowError, notifySuccess, notifyError, notifyHttpError,
} from '@/api'
import {
    DataGrid, Modal, ModalRequest, Breadcrumbs, ViewsGrid, NotificationLink, Dropdown,
} from '@/components'
import { getColumnDefs } from '@/components/DataGrid/utils'
import { FORMATS, ISO_DATETIME, TYPES } from '@/entities/fields'
import {
    HistoricField, FiltersSidebar, FiltersTopbar, RowPopup,
} from './components'
import Grid from './grid'

export const MODAL_TYPES = {
    AUDIT: 'AUDIT',
    DELETE_SELECTION: 'DELETE_SELECTION',
    MERGE_SELECTION: 'MERGE_SELECTION',
    EDIT_SELECTION: 'EDIT_SELECTION',
}

export default {
    name: 'AdminEntityListScreen',
    components: {
        DataGrid,
        Modal,
        ModalRequest,
        Breadcrumbs,
        HistoricField,
        ViewsGrid,
        NotificationLink,
        Dropdown,
        FiltersSidebar,
        FiltersTopbar,
        RowPopup,
    },
    metaInfo () {
        return {
            title: this.$t(`admin.navigation.entities.${this.entityKey}`),
        }
    },
    data () {
        this.exportOptions = [
            { key: 'title', label: this.$t('entities.actions.exportOptions.title') },
            { key: 'camel', label: this.$t('entities.actions.exportOptions.camel') },
        ]
        return {
            // List request
            columns: [],
            selectGroups: undefined,
            totalResults: 0,
            isLoading: false,

            // Filters
            filterModel: {},
            isFiltersSidebarOpen: false,
            excludedEntities: [],

            // Edit Cell request
            editionData: undefined,
            isEditingCell: false,
            isLoadingEditingCell: false,

            // Views
            activeView: undefined,

            // Grid
            node: undefined,
            dataGridConfig: {
                ...Grid.Config,
                editable: true,
                contextMenu: this.contextMenu,
            },

            // Modals
            modal: undefined,
            modals: MODAL_TYPES,
            dissmisableAudit: false,

            // Custom notification
            notification: {
                group: 'admin-entity-list',
            },

            // Export
            exportLoading: false,

            // Popup
            popupVisible: false,
            popupProperties: [],
            popupData: [],
            popupTimeout: undefined,
        }
    },
    computed: {
        ...mapState({
            token: state => (state.auth.token || {}).token,
            relatedFilters: ({ filters }) => filters.relatedFilters,
            temporalUnpinned: ({ filters }) => filters.temporalUnpinned,
        }),
        breadcrumbs () {
            return [{
                key: this.entityKey,
                label: this.$t(`admin.navigation.entities.${this.entityKey}`),
            }]
        },
        columnDefs () {
            const { columns, selectGroups } = this
            return getColumnDefs({ columns, selectGroups, formatDate: 'DD/MM/YYYY' })
        },
    },
    beforeCreate () {
        const { entityKey } = this.$route.params
        this.entityKey = entityKey
    },
    created () {
        this.initFilterModel(this.entityKey)
        this.initPopupProperties(this.entityKey)
    },
    mounted () {
        this.grid = this.$refs.dataGrid
        if (this.$route.query.view) this.activeView = Number(this.$route.query.view)
    },
    beforeRouteUpdate (to, from, next) {
        const { saveTemporalUnpinned } = this.$refs.filtersTopbar
        if (to.params.entityKey !== from.params.entityKey) saveTemporalUnpinned()
        next()
    },
    methods: {
        contextMenu ({ node, api, column }) {
            const rows = api.getSelectedNodes()
            const multiple = (rows || []).length > 1
            const fieldKey = column && column.getId()
            const renderIcon = icon => `<i class="WIcon icon-${icon} ag-icon" />`

            return [
                'copy',
                'paste',
                'separator',
                ...(multiple ? [
                    {
                        name: this.$t('entities.actions.deleteSelection'),
                        action: () => this.handleRequest({ fieldKey, rows }, this.modals.DELETE_SELECTION),
                        icon: renderIcon('trash'),
                    },
                ] : [
                    {
                        name: this.$t('entities.actions.viewDetail'),
                        action: () => {
                            const route = this.$router.resolve({ name: 'AdminEntityItemDetail', params: { entityKey: this.entityKey, id: node.data.id } })
                            window.open(route.href, '_blank')
                        },
                        icon: renderIcon('eye'),
                    },
                    {
                        name: this.$t('entities.actions.edit'),
                        action: () => this.$router.push({ name: 'AdminEntityItemEdit', params: { entityKey: this.entityKey, id: node.data.id } }),
                        icon: renderIcon('edit'),
                    },
                    {
                        name: this.$t('entities.actions.delete'),
                        action: () => this.handleRequest({ fieldKey, rows: [node] }, this.modals.DELETE_SELECTION),
                        icon: renderIcon('trash'),
                    },
                    'separator',
                    {
                        name: this.$t('entities.actions.audit'),
                        action: () => this.handleRequest({ fieldKey, rows: node }, this.modals.AUDIT),
                        icon: renderIcon('list'),
                    },
                ]),
            ]
        },
        /*
        * Filters
        */
        filterTable () {
            this.grid.gridApi.onFilterChanged()
        },
        initFilterModel (entityKey) {
            const prevPinned = this.relatedFilters.find(e => e.entity === entityKey)
            this.filterModel = prevPinned?.filters || {}
        },
        applyPreset () {
            this.initFilterModel(this.entityKey)
            this.filterTable()
        },
        applyFilter (field, model) {
            this.filterModel = { ...this.filterModel, [field]: model }
            this.filterTable()
        },
        cleanFilter (field, entity = this.entityKey) {
            if (entity === this.entityKey) {
                const { [field]: _, ...restModel } = this.filterModel
                this.filterModel = restModel
            }
            this.filterTable()
        },
        cleanAllFilters () {
            this.filterModel = {}
            this.filterTable()
        },
        getCurrentRelatedFilters () {
            const { entityKey, relatedFilters = [] } = this
            return relatedFilters.filter(f => f.entity !== entityKey)
        },
        handleExport ({ key: keysFormat }) {
            this.exportLoading = true
            const { entityKey, filterModel = {} } = this
            const allColumns = this.grid.gridApi.columnController.getColumnState()
            const params = {
                columns: allColumns.filter(c => c.hide !== true).map(({ colId, hide }) => ({ colId, hide })),
                relatedFilters: this.getCurrentRelatedFilters(),
                filters: filterModel,
            }
            apiRequest(Api().templates.downloadData(entityKey, keysFormat, params))
                .then((res) => { fileDownload(res, `${entityKey}_DATA.csv`) })
                .catch(rethrowError(notifyError()))
                .finally(() => { this.exportLoading = false })
        },
        /*
        * Popup
        */
        initPopupProperties (entityKey) {
            apiRequest(Api().entities.schema({ entityKey }))
                .then(({ data }) => { this.popupProperties = data.popupProperties })
                .catch(rethrowError(notifyError()))
        },
        handlePopup (visible, { data }) {
            this.popupData = this.getPopupData(data)
            clearTimeout(this.popupTimeout)
            if (visible) this.popupTimeout = setTimeout(() => { this.popupVisible = true }, 500)
            else {
                this.popupVisible = false
                this.popupTimeout = undefined
            }
        },
        getPropertyLabel (property) {
            const allFields = this.columnDefs.map(c => c.children).flat(2)
            return allFields.find(({ field }) => field === property)?.headerName
        },
        getPopupData (data) {
            return this.popupProperties.map((property) => {
                const isDate = moment(data[property], ISO_DATETIME, true).isValid()
                const value = isDate ? moment(data[property]).format(FORMATS[TYPES.DATE]) : data[property]
                return { property, value, label: this.getPropertyLabel(property) }
            })
        },
        /*
        * Modals
        */
        handleCleanModal () {
            this.modal = undefined
            this.node = undefined
            this.editionData = undefined
        },
        handleRequest (node, modal) {
            this.node = node
            if (modal) this.modal = modal
        },
        /*
        * Notification
        */
        showNotificationUpdate () {
            this.$notify({
                type: 'success',
                group: this.notification.group,
                title: this.$t('entities.modals.updateSuccess.title'),
                text: this.$t('entities.modals.updateSuccess.text'),
                data: {
                    link: {
                        text: this.$t('entities.modals.updateSuccess.link'),
                        onClick: this.handleClickReload,
                    },
                },
                duration: 5000,
            })
        },
        closeNotificationUpdate () {
            this.$notify({
                group: this.notification.group,
                clean: true,
            })
        },
        handleClickReload () {
            apiRequest(Api().imports.create({ source: 'computed' }))
                .then(() => {
                    this.closeNotificationUpdate()
                    setTimeout(() => notifySuccess(this.$t('entities.modals.reload'))(), 500)
                })
                .catch(rethrowError(notifyError()))
        },
        /*
        * Views
        */
        handleChangeView (view) {
            const navigation = !view ? { ...this.$router } : { query: { view } }
            this.$router.replace(navigation).catch(() => {})
        },
        /*
        * Fields
        */
        handleChangeField (data) {
            this.node.rows.setData(data)
            this.handleCleanModal()
        },
        handleCellChanged ({
            node, colDef, data, oldValue, newValue,
        }) {
            if (Grid.compareValues(newValue, oldValue, colDef.type) && !this.isEditingCell) {
                this.editionData = {
                    fieldKey: colDef.field, rowKey: data.id, oldValue, newValue,
                }
                this.handleRequest(node, this.modals.EDIT_SELECTION)
                this.isEditingCell = true
            } else {
                this.isEditingCell = false // End of field edit
            }
        },
        /*
        * Rows
        */
        handleCancelEdition () {
            this.node.setDataValue(this.editionData.fieldKey, this.editionData.oldValue)
            this.handleCleanModal()
        },
        /*
        * Get Entities
        */
        async handleReady () {
            Grid.Handler({
                fetch: this.getData,
                error: notifyError(),
                grid: this.grid,
            })
        },
        async getData (initParams = {}) {
            this.isLoading = true
            const { entityKey, filterModel = {} } = this
            const fixedParams = typeof initParams === 'string' ? JSON.parse(initParams) : initParams
            const params = {
                ...fixedParams,
                filterModel,
                relatedFilters: this.getCurrentRelatedFilters(),
            }
            return apiRequest(Api().outputs.post({ entityKey, params }))
                .then(({ meta, data }) => {
                    if (!this.columns.length) {
                        this.selectGroups = data.selectGroups
                        this.columns = Grid.normalizeColumns(data.columns)
                        this.excludedEntities = meta.excludedEntities
                    }
                    this.totalResults = meta.totalRows
                    return { data: data.rows, meta }
                })
                .finally(() => (this.isLoading = false))
        },
        /*
        * Update Row
        */
        async getSelected (id) {
            return apiRequest(Api().outputs.get({ entityKey: this.entityKey, params: { id } }))
                .then(({ meta, data }) => {
                    const { rows } = data || {}
                    const [row] = rows || []
                    return { ok: true, data: row }
                })
                .catch(error => ({ ok: false, error }))
        },
        async editSelected () {
            const { rowKey: id, fieldKey, newValue: value } = this.editionData
            const params = { type: this.entityKey, row: { id, [fieldKey]: value } }
            const options = { timeout: 0 }
            const onSuccess = () => {
                this.showNotificationUpdate()
                this.handleCleanModal()
            }
            const onError = (error) => {
                this.handleCancelEdition()
                rethrowError(notifyHttpError())(error)
            }

            this.isLoadingEditingCell = true
            apiRequest(Api().imports.create(params, options))
                .then(async () => {
                    const { ok, data, error } = await this.getSelected(id)
                    if (ok) {
                        const dataValue = data[fieldKey] || ''
                        this.node.setDataValue(fieldKey, dataValue)
                        onSuccess()
                    } else {
                        onError(error)
                    }
                    // Fix don't check if value changed
                    setTimeout(() => (this.isEditingCell = false), 1500)
                })
                .catch(onError)
                .finally(() => (this.isLoadingEditingCell = false))
        },
        /*
        * Delete Row
        */
        async removeSelecteds () {
            const { rows } = this.node
            const rowIds = rows.map(({ data }) => data.id).filter(id => !!id)
            const { length } = rowIds
            const request = {
                ...(length > 1 ? {
                    action: 'deleteMultiple',
                    params: { entityKey: this.entityKey, params: { rowIds } },
                } : {
                    action: 'delete',
                    params: { entityKey: this.entityKey, id: rowIds[0] },
                }),
            }

            if (!length) {
                this.handleCleanModal()
                return
            }

            this.isLoadingEditingCell = true
            apiRequest(Api().outputs[request.action](request.params))
                .then(() => this.node.rows.forEach(this.grid.removeRow))
                .catch(rethrowError(notifyError()))
                .finally(() => {
                    this.handleCleanModal()
                    this.isLoadingEditingCell = false
                })
        },
        /*
        * Merge Rows
        */
        async mergeSelecteds () {
            const params = { rowKeys: this.node.rows.map(({ id }) => id) }
            apiRequest(Api().entities.merge(this.entityKey, params))
                .then(this.getData)
                .catch(rethrowError(notifyError()))
                .finally(this.handleCleanModal)
        },
    },
}
</script>

<style lang="scss" scoped>
.AdminEntityListHeader {
    display: flex;
    justify-content: space-between;
}
.DataGrid {
	@include admin-screen-height(190px);
}
.AuditModal {
    &::v-deep .Dialog {
        max-width: 650px;
    }
}
.FiltersButton {
    display: flex;
    flex-direction: row;
    align-items: center;
    .FiltersLabel {
        display: flex;
        flex-direction: row;
        align-items: center;
        padding-left: 8px;
        border-left: 1px solid $gray-04;
    }
}
</style>
